// Da dove viene l'indirizzo del cliente (2026-09-18).
//
// Le pratiche BSC arrivano quasi sempre inoltrate da un portale, e l'indirizzo
// del cliente si puo' ricavare in modi di solidita' molto diversa: leggerlo
// dall'intestazione del primo invio e' un fatto, dedurlo per somiglianza col
// nome della firma e' un'ipotesi. Senza questa indicazione l'operatore vede due
// indirizzi identici in pagina e non ha modo di sapere quale verificare.
//
// Il valore arriva da bsc_data.client_email_source (src/bsc/client_extract.py).

type Source = "chain" | "internal" | "signature" | "body" | "sender" | "llm";

// `solid` distingue il dato certo da quello dedotto. Le pratiche interne non
// sono ne' l'uno ne' l'altro: sono un caso diverso — non arrivano da un
// cliente, le apre qualcuno di casa — e hanno un tono loro.
const SOURCES: Record<Source, { label: string; title: string; solid: boolean; internal?: boolean }> = {
  chain: {
    label: "primo invio",
    title: "Letto dall'intestazione del messaggio originale (Da:/Inviato:): è il mittente che ha aperto la pratica.",
    solid: true,
  },
  internal: {
    label: "interna",
    title: "Pratica interna: la mail arriva da un indirizzo Frabemar, non da un cliente né dal portale. Il nome accanto è di chi l'ha inviata.",
    solid: true,
    internal: true,
  },
  signature: {
    label: "da firma",
    title: "Dedotto: è l'indirizzo del corpo che somiglia al nome della firma. Da verificare.",
    solid: false,
  },
  body: {
    label: "dal testo",
    title: "Primo indirizzo esterno trovato nel corpo, senza intestazione di inoltro riconosciuta. Da verificare.",
    solid: false,
  },
  sender: {
    label: "mittente",
    title: "Indirizzo di chi ha inviato il messaggio: se la pratica è stata inoltrata, potrebbe non essere il cliente.",
    solid: false,
  },
  llm: {
    label: "proposto",
    title: "Proposto dal modello leggendo la firma, senza riscontro nel testo. Da verificare.",
    solid: false,
  },
};

/** Etichetta leggibile di una origine, per filtri e legende. */
export const CLIENT_SOURCE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Tutte" },
  ...(Object.keys(SOURCES) as Source[]).map((k) => ({ value: k, label: SOURCES[k].label })),
  { value: "none", label: "senza origine" },
];

export function ClientSourceChip({
  source,
  iconOnly = false,
}: {
  source: string | null | undefined;
  /** Solo l'icona colorata: in colonna dedicata il testo ruberebbe spazio
   *  alle colonne che l'operatore legge davvero. Il significato resta nel
   *  tooltip, che riporta etichetta + spiegazione. */
  iconOnly?: boolean;
}) {
  const info = source ? SOURCES[source as Source] : undefined;
  if (!info) {
    // Niente origine: un trattino, coerente con le altre colonne vuote.
    return iconOnly ? <span className="text-muted">—</span> : null;
  }
  return (
    <span
      title={iconOnly ? `${info.label} — ${info.title}` : info.title}
      style={{
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        gap: 2,
        fontSize: "var(--font-size-xs)",
        fontWeight: 600,
        background: info.internal
          ? "var(--indaco-info-light)"
          : info.solid
            ? "var(--indaco-success-light)"
            : "var(--indaco-warning-light)",
        color: info.internal
          ? "var(--indaco-info)"
          : info.solid
            ? "var(--indaco-success)"
            : "var(--indaco-warning)",
        borderRadius: iconOnly ? "50%" : 10,
        padding: iconOnly ? 3 : "1px 6px",
        whiteSpace: "nowrap",
        cursor: "default",
      }}
    >
      <span className="material-symbols-outlined" style={{ fontSize: iconOnly ? 15 : 12 }}>
        {info.internal ? "edit_note" : info.solid ? "verified" : "help"}
      </span>
      {!iconOnly && info.label}
    </span>
  );
}
